import type { IncidentHistoryEntryResponse } from '@prerender/diagnostics-api';
import { Fragment, ReactNode, useId } from 'react';
import { Table } from 'react-bootstrap';

import { formatDetectedAt } from './formatDetectedAt';
import HistoryChange from './HistoryChange';
import HistoryStateAfter from './HistoryStateAfter';
import { crawlerOfEntry, historyAuthorLabel } from './historyLabels';
import HistoryTransition, { isTransition } from './HistoryTransition';
import { relativeTimeSince } from './relativeTimeSince';

type IncidentHistoryProps = {
  history: readonly IncidentHistoryEntryResponse[];
  now: Date;
};

type HistoryLine = {
  readonly position: number;
  readonly entries: readonly IncidentHistoryEntryResponse[];
};

const HISTORY_TITLE = 'History';
const HISTORY_HINT = 'One line per check that changed something. Checks that change nothing add no line.';
const NOTHING_YET = 'No history yet';
const DETECTED_BY_CRAWLERS = 'Detected: ';
const CRAWLER_SEPARATOR = '; ';

const COLUMNS = ['When', 'Who', 'What changed', 'After'];

const sameRun = (one: IncidentHistoryEntryResponse, other: IncidentHistoryEntryResponse): boolean =>
  one.runReference !== null && one.runReference === other.runReference;

// The endpoint answers oldest first and only ever appends, so a line's position from the
// oldest is its identity: two lines can share their time, kind and domains.
function linesOf(history: readonly IncidentHistoryEntryResponse[]): HistoryLine[] {
  return history.reduce<HistoryLine[]>((lines, entry, position) => {
    const last = lines[lines.length - 1];
    if (last !== undefined && sameRun(last.entries[0], entry)) {
      return [...lines.slice(0, -1), { ...last, entries: [...last.entries, entry] }];
    }

    return [...lines, { position, entries: [entry] }];
  }, []);
}

function DetectedClause({ openings }: { openings: readonly IncidentHistoryEntryResponse[] }) {
  if (openings.every((opening) => crawlerOfEntry(opening.detail) === null)) {
    return (
      <>
        {openings.map((opening, index) => (
          <HistoryChange key={index} entry={opening} />
        ))}
      </>
    );
  }

  return (
    <>
      {DETECTED_BY_CRAWLERS}
      {openings.map((opening, index) => (
        <Fragment key={index}>
          {index > 0 && CRAWLER_SEPARATOR}
          <HistoryChange entry={opening} />
        </Fragment>
      ))}
    </>
  );
}

function ChangeClause({ entry }: { entry: IncidentHistoryEntryResponse }) {
  return isTransition(entry) ? <HistoryTransition entry={entry} /> : <HistoryChange entry={entry} />;
}

function clausesOf(entries: readonly IncidentHistoryEntryResponse[]): ReactNode[] {
  const openings = entries.filter(({ eventType }) => eventType === 'created');
  const changes = entries.filter(({ eventType }) => eventType !== 'created');

  return [
    ...(openings.length === 0 ? [] : [<DetectedClause key="detected" openings={openings} />]),
    ...changes.map((entry, index) => <ChangeClause key={index} entry={entry} />),
  ];
}

function HistoryClauses({ entries }: { entries: readonly IncidentHistoryEntryResponse[] }) {
  const clauses = clausesOf(entries);

  if (clauses.length === 1) {
    return <>{clauses}</>;
  }

  return (
    <ul className="diagnostics-history-changes">
      {clauses.map((clause, index) => (
        <li key={index}>{clause}</li>
      ))}
    </ul>
  );
}

function IncidentHistory({ history, now }: IncidentHistoryProps) {
  const titleId = useId();
  const latestFirst = linesOf(history).reverse();

  return (
    <section className="diagnostics-detail-history" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {HISTORY_TITLE}
      </h2>
      <p className="diagnostics-history-hint">{HISTORY_HINT}</p>
      <Table responsive className="diagnostics-table diagnostics-history-table" aria-label={HISTORY_TITLE}>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {latestFirst.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length}>{NOTHING_YET}</td>
            </tr>
          )}
          {latestFirst.map(({ position, entries }) => {
            const [first] = entries;
            const occurredAt = new Date(first.occurredAt);

            return (
              <tr key={position}>
                <td className="diagnostics-cell-compact">
                  {formatDetectedAt(occurredAt)}
                  <span className="diagnostics-history-age">{relativeTimeSince(occurredAt, now)}</span>
                </td>
                <td>{historyAuthorLabel(first.actor, first.detail)}</td>
                <td>
                  <HistoryClauses entries={entries} />
                </td>
                <td className="diagnostics-history-after">
                  <HistoryStateAfter stateAfter={entries[entries.length - 1].stateAfter} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </section>
  );
}

export default IncidentHistory;
